import { useEffect, useCallback, useState, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { useUserCredentials } from '../hooks/useUserCredentials';
import { deleteUserCredential } from '../api/keycloak-admin';
import { keycloak } from '../auth/keycloak';
import { AUTH_METHODS } from '../constants/auth-methods';
import { COLORS, FONTS, SHADOWS } from '../styles/tokens';
import { CredentialList, AddMethodCards } from './SecurityModalContent';
import { openMfaPopup, MFA_COMPLETE_MESSAGE_TYPE, POPUP_POLL_INTERVAL_MS } from '../utils/popup';
import { MfaSetupOverlay, SUCCESS_DISPLAY_MS } from './MfaSetupOverlay';

type ModalView = 'default' | 'waiting' | 'success';

/** PostMessage event shape from mfa-callback.html */
interface MfaCompleteEvent {
  type: string;
}

interface SecurityModalProps {
  onClose: () => void;
}

/** Modal for viewing/managing configured auth credentials */
export function SecurityModal({ onClose }: SecurityModalProps): React.JSX.Element {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const userId = keycloak.tokenParsed?.sub;
  const { credentials, loading, refresh } = useUserCredentials(userId);

  const [view, setView] = useState<ModalView>('default');
  const [activeMethodLabel, setActiveMethodLabel] = useState('');
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Close any open popup and clear the poll interval */
  const cleanupPopup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  /** Handle postMessage from mfa-callback.html */
  useEffect(() => {
    function handleMessage(event: MessageEvent<MfaCompleteEvent>): void {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== MFA_COMPLETE_MESSAGE_TYPE) return;

      cleanupPopup();
      setView('success');
      refresh();

      setTimeout(() => {
        setView('default');
      }, SUCCESS_DISPLAY_MS);
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [cleanupPopup, refresh]);

  /** Close on Escape — also cancels popup if active */
  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        if (view !== 'default') {
          cleanupPopup();
          setView('default');
        } else {
          onClose();
        }
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, view, cleanupPopup]);

  /** Cleanup on unmount */
  useEffect(() => {
    return () => cleanupPopup();
  }, [cleanupPopup]);

  /** Remove a credential */
  const handleRemove = useCallback(async (credentialId: string) => {
    if (!userId) return;
    try {
      await deleteUserCredential(token, userId, credentialId);
      refresh();
    } catch (err) {
      console.error('[SecurityModal] Failed to remove credential:', err);
    }
  }, [token, userId, refresh]);

  /** Open MFA setup in a popup, falling back to redirect if blocked */
  async function handleAddMethod(methodId: string): Promise<void> {
    const method = AUTH_METHODS.find(m => m.id === methodId);
    if (!method) return;

    if (method.id === 'cac') {
      onClose();
      return;
    }

    if (!method.keycloakAction) return;

    // Close any existing popup before opening a new one
    cleanupPopup();

    const callbackUrl = `${window.location.origin}/mfa-callback.html`;
    const loginUrl = await keycloak.createLoginUrl({
      action: method.keycloakAction,
      redirectUri: callbackUrl,
    });

    const popup = openMfaPopup(loginUrl);

    if (!popup) {
      // Popup blocked — fall back to full-page redirect
      keycloak.login({ action: method.keycloakAction });
      return;
    }

    popupRef.current = popup;
    setActiveMethodLabel(method.label);
    setView('waiting');

    // Poll for popup being closed without completing (user clicked X)
    pollRef.current = setInterval(() => {
      if (popup.closed) {
        cleanupPopup();
        setView(prev => (prev === 'waiting' ? 'default' : prev));
      }
    }, POPUP_POLL_INTERVAL_MS);
  }

  /** Cancel the in-progress popup setup */
  function handleCancelSetup(): void {
    cleanupPopup();
    setView('default');
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={view === 'default' ? onClose : undefined}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 50,
        }}
      />

      {/* Modal card */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          backgroundColor: colors.card,
          borderRadius: '12px',
          boxShadow: isDark ? SHADOWS.neumorphicDark : SHADOWS.neumorphicLight,
          zIndex: 51,
          padding: '32px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{
            fontFamily: FONTS.heading,
            fontSize: '14px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: colors.text,
            margin: 0,
          }}>
            ACCOUNT SECURITY
          </h2>
          <button
            onClick={view === 'default' ? onClose : handleCancelSetup}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: colors.textSecondary,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Overlay states (waiting / success) */}
        {view !== 'default' && (
          <MfaSetupOverlay
            view={view}
            methodLabel={activeMethodLabel}
            onCancel={handleCancelSetup}
          />
        )}

        {/* Default content: configured methods + add method */}
        {view === 'default' && (
          <>
            <CredentialList
              credentials={credentials}
              loading={loading}
              onRemove={handleRemove}
              isDark={isDark}
              colors={colors}
            />
            <AddMethodCards
              onAdd={handleAddMethod}
              isDark={isDark}
              colors={colors}
            />
          </>
        )}
      </div>
    </>
  );
}
